import { useState, useCallback } from "react";
import useStore from "../store/useStore";
import { referredPlanDetails } from "../pages/referCarePlan/service";
import { Patient } from "../types";
import { getDecodedToken } from "../utils/localStorage";

interface PaginationState {
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
}

const useReferredPatients = () => {
  const {
    setReferredPatients,
    doctorDetails,
    setTotalPatientsReferred,
    setTotalplanPurchased,
  } = useStore();
  const tokenData: any = getDecodedToken();
  const { b2cId = "" } = doctorDetails?.user || tokenData?.user || {};
  const [referredPlanData, setReferredPlanData] = useState<Patient[]>([]);
  const [loader, setLoader] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({
    page: 1,
    per_page: 10,
    total: 0,
    total_pages: 0,
  });
  const [hasMoreData, setHasMoreData] = useState(true);

  const getReferredPlanDetails = useCallback(
    async ({
      status,
      search,
      page = 1,
      per_page = 10,
      reset = false,
    }: {
      status?: string;
      search?: string;
      page?: number;
      per_page?: number;
      reset?: boolean;
    }) => {
      if (reset) {
        setLoader(true);
        setReferredPlanData([]);
        setPagination({ page: 1, per_page, total: 0, total_pages: 0 });
        setHasMoreData(true);
      } else {
        setLoadingMore(true);
      }

      try {
        const res: any = await referredPlanDetails(
          {
            b2cId,
            status,
            search,
          },
          page,
          per_page
        );

        if (res?.code === 1 && res?.data) {
          const newData = res.data.data || [];
          const paginationData = res.data.pagination || {};

          let updatedData: Patient[];
          if (reset || page === 1) {
            updatedData = newData;
            setReferredPlanData(newData);
          } else {
            setReferredPlanData((prev) => {
              updatedData = [...prev, ...newData];
              return updatedData;
            });
          }

          setPagination(paginationData);
          setReferredPatients(updatedData!);
          setTotalPatientsReferred(
            res?.data?.totalPatientsReferred || paginationData.total || 0
          );
          setTotalplanPurchased(res?.data?.totalplanPurchased || 0);

          // Check if there's more data to load
          setHasMoreData(paginationData.page < paginationData.total_pages);
        }
      } catch (error) {
        console.error("Error fetching referred plan details:", error);
      } finally {
        setLoader(false);
        setLoadingMore(false);
      }
    },
    [
      b2cId,
      setReferredPatients,
      setTotalPatientsReferred,
      setTotalplanPurchased,
    ]
  );

  const loadMoreData = useCallback(
    async (search?: string, status?: string) => {
      if (!hasMoreData || loadingMore) return;

      const nextPage = pagination.page + 1;
      await getReferredPlanDetails({
        search,
        status,
        page: nextPage,
        per_page: pagination.per_page,
        reset: false,
      });
    },
    [
      pagination.page,
      pagination.per_page,
      hasMoreData,
      loadingMore,
      getReferredPlanDetails,
    ]
  );

  const resetAndFetch = useCallback(
    async (search?: string, status?: string) => {
      await getReferredPlanDetails({
        search,
        status,
        page: 1,
        per_page: 10,
        reset: true,
      });
    },
    [getReferredPlanDetails]
  );

  return {
    loader,
    loadingMore,
    getReferredPlanDetails,
    loadMoreData,
    resetAndFetch,
    referredPlanData,
    hasMoreData,
    pagination,
  };
};

export default useReferredPatients;
